// frontend/src/components/checklists/ProjectTemplateSelectChecklist.tsx
import React from "react";
import { Badge, Box, Code, Group, Loader, Select, Stack, Text, TextInput } from "@mantine/core";
import { useGetChecklistProjectTemplatesQuery } from "../../openapi";
import { useAppSelector } from "../hooks";
import { FailedAlert } from "../components";

const DEFAULT_PROJECT_NO = "10216575";

const isValidProjectNo = (v: string) => /^\d{8}$/.test(v.trim());

const ProjectTemplateSelectChecklist: React.FC = () => {
  const [projectNo, setProjectNo] = React.useState<string>(DEFAULT_PROJECT_NO);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<number | null>(null);
  const xApiKey = useAppSelector((state) => state.globalReducer.xApiKey);

  const valid = isValidProjectNo(projectNo) && xApiKey != null;

  const {
    data: templates = [],
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    startedTimeStamp,
    fulfilledTimeStamp,
  } = useGetChecklistProjectTemplatesQuery(
    { projectNo: projectNo.trim() },
    {
      skip: !valid,
    },
  );

  // Om projectNo endres, nullstill valgt template hvis den ikke finnes i ny liste
  React.useEffect(() => {
    if (selectedTemplateId == null) return;
    const stillExists = templates.some((t) => t.id === selectedTemplateId);
    if (!stillExists) setSelectedTemplateId(null);
  }, [templates, selectedTemplateId]);

  const selectOptions = React.useMemo(
    () =>
      templates.map((template) => ({
        value: template.id.toString(),
        label: `${template.id} - ${template.name}`,
      })),
    [templates],
  );

  const busy = isLoading || isFetching;

  const requestTime =
    isSuccess && startedTimeStamp && fulfilledTimeStamp ? fulfilledTimeStamp - startedTimeStamp : null;

  const lastRequestTimestamp = fulfilledTimeStamp ? new Date(fulfilledTimeStamp).toLocaleString("no-NO") : null;

  return (
    <Box>
      {isError && <FailedAlert />}
      <Group justify="space-between" align="center" mb="xs">
        <Stack gap={2}>
          <Text fw={500}>Velg prosjekt-mal:</Text>
          <Text size="sm" c="dimmed">
            Henter automatisk når projectNo er <Code>8</Code> siffer.
          </Text>
        </Stack>
      </Group>

      <Stack>
        <TextInput
          label="ProjectNo"
          value={projectNo}
          onChange={(e) => setProjectNo(e.currentTarget.value)}
          placeholder={DEFAULT_PROJECT_NO}
          description="Må være nøyaktig 8 siffer for å trigge kall."
          error={projectNo.length > 0 && !valid ? "Ugyldig format (forventet 8 siffer)" : undefined}
        />

        {busy ? (
          <Loader size="sm" />
        ) : (
          <Select
            label="Maler"
            placeholder={valid ? "Ingen mal valgt" : "Skriv 8 siffer for å hente maler"}
            data={selectOptions}
            searchable
            clearable
            maxDropdownHeight={300}
            leftSection={<i className="fa fa-list" />}
            value={selectedTemplateId !== null ? String(selectedTemplateId) : null}
            onChange={(value) => setSelectedTemplateId(value ? Number(value) : null)}
            disabled={!valid || templates.length === 0}
          />
        )}

        {!valid && (
          <Badge color="gray" leftSection={<i className="fa fa-info-circle" />}>
            Skip: venter på gyldig projectNo
          </Badge>
        )}

        {isSuccess && valid && (
          <Stack gap="xs">
            <Badge color="green" leftSection={<i className="fa fa-check" />}>
              Vellykket anrop ({templates.length} maler)
            </Badge>

            {requestTime !== null && (
              <Text size="sm" c="dimmed">
                Anropstid: {requestTime.toFixed(2)} ms
              </Text>
            )}

            {lastRequestTimestamp && (
              <Text size="sm" c="dimmed">
                Sist kjørt: {lastRequestTimestamp}
              </Text>
            )}
          </Stack>
        )}

        {isError && valid && (
          <Stack gap="xs">
            <Badge color="red" leftSection={<i className="fa fa-times" />}>
              Feil ved anrop
            </Badge>
            <Text size="sm" c="dimmed">
              {typeof error === "object" ? JSON.stringify(error) : String(error)}
            </Text>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default ProjectTemplateSelectChecklist;
